import React, { useEffect, useMemo, useState } from "react";
import { StepProps } from "../common/types";
import { useAuth } from "../../context/AuthContext";
import { getAllLocations, getAllZones, getHeadquarterByTenantZone } from "../../api/master";
import Select from "react-select";

type ZoneType = {
  id: string | number;
  country_id: string | number;
  tenant_id: string | number;
  name: string;
  latitude: string;
  longitude: string;
  created_at?: string;
  updated_at?: string;
};

type HeadquarterType = {
  id: string | number;
  zone_id: string | number;
  tenant_id: string | number;
  name: string;
  latitude: string;
  longitude: string;
  created_at?: string;
  updated_at?: string;
};

type LocationType = {
  id: string | number;
  head_quarter_id: string | number;
  tenant_id: string | number;
  name: string;
  is_hq?: boolean;
  latitude: string;
  longitude: string;
  created_at?: string;
  updated_at?: string;
};

export const ContactInfoTab: React.FC<StepProps> = ({ formik, currentEmployee }) => {
  const { user } = useAuth();
  const [zones, setZones] = useState<ZoneType[]>([]);
  const [headquarters, setHeadquarters] = useState<HeadquarterType[]>([]);
  const [allLocations, setAllLocations] = useState<LocationType[]>([]);
  const [exStations, setExStations] = useState<LocationType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch all initial data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch zones and locations in parallel
        const [zonesResponse, locationsResponse] = await Promise.all([
          getAllZones(user.tenant_id),
          getAllLocations(user.tenant_id)
        ]);

        setZones(zonesResponse);
        setAllLocations(locationsResponse);

        // Initialize form with current employee data after data is loaded
        if (currentEmployee) {
          await initializeFormWithEmployeeData(currentEmployee, zonesResponse, locationsResponse);
        }

      } catch (error) {
        console.log("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Initialize form with employee data
  const initializeFormWithEmployeeData = async (
    employee: any,
    zonesList: ZoneType[],
    locationsList: LocationType[]
  ) => {
    try {
      // Set basic contact fields
      if (employee.mobile) formik.setFieldValue("mobile", employee.mobile);
      if (employee.alternate_mobile) formik.setFieldValue("alternate_mobile", employee.alternate_mobile);
      if (employee.email) formik.setFieldValue("email", employee.email);
      if (employee.current_address) formik.setFieldValue("current_address", employee.current_address);
      if (employee.permanent_address) formik.setFieldValue("permanent_address", employee.permanent_address);
      if (employee.zip) formik.setFieldValue("zip", employee.zip);

      // Set zone
      if (employee.zone_id) {
        formik.setFieldValue("zone_id", employee.zone_id);

        // Fetch headquarters for this zone
        const hqResponse = await getHeadquarterByTenantZone(user.tenant_id, Number(employee.zone_id));
        setHeadquarters(hqResponse);

        // Set multiple HQs
        const hqIds: number[] = Array.isArray(employee.head_quarters) && employee.head_quarters.length > 0
          ? employee.head_quarters.map((h: any) => Number(h.id ?? h))
          : employee.head_quarter_id ? [Number(employee.head_quarter_id)] : [];

        if (hqIds.length > 0) {
          formik.setFieldValue("head_quarters", hqIds);
          formik.setFieldValue("head_quarter_id", hqIds[0]);
          const filteredStations = locationsList.filter(loc => hqIds.includes(Number(loc.head_quarter_id)));
          setExStations(filteredStations);
        }
      }

      // Set Out Station HQs (employee_out_hq) — the HQ-flagged locations gating Out Stations
      if (employee.out_station_hqs && Array.isArray(employee.out_station_hqs)) {
        const outHqIds = employee.out_station_hqs.map((h: any) => Number(h.id ?? h)).filter(Boolean);
        formik.setFieldValue("out_station_hqs", outHqIds);
      }

      // Set station arrays directly - important!
      if (employee.ex_stations && Array.isArray(employee.ex_stations)) {
        formik.setFieldValue("ex_stations", employee.ex_stations);
      }

      if (employee.out_stations && Array.isArray(employee.out_stations)) {
        formik.setFieldValue("out_stations", employee.out_stations);
      }

      // Set assigned locations
      if (employee.assigned_locations && Array.isArray(employee.assigned_locations)) {
        const ids = employee.assigned_locations.map((l: any) => Number(l.id ?? l));
        formik.setFieldValue("assigned_locations", ids);
      }

    } catch (error) {
      console.log("Error initializing form:", error);
    } finally {
      setIsInitialized(true);
    }
  };

  const getheadquarterByZone = async (zone_id: number) => {
    try {
      const response = await getHeadquarterByTenantZone(user.tenant_id, zone_id);
      setHeadquarters(response);
    } catch (error) {
      console.log("Error fetching headquarters:", error);
    }
  };

  // Filter Ex-Station candidates by the employee's own assigned HQs
  const filterStationsByHQs = (hqIds: number[]) => {
    if (!hqIds.length || !allLocations.length) {
      setExStations([]);
      return;
    }
    setExStations(allLocations.filter(loc => hqIds.includes(Number(loc.head_quarter_id))));
  };

  // Handle HQ change — head_quarters is now an array of IDs
  useEffect(() => {
    const hqIds: number[] = Array.isArray(formik.values.head_quarters)
      ? formik.values.head_quarters.map(Number).filter(Boolean)
      : [];
    filterStationsByHQs(hqIds);
    // keep head_quarter_id = first selection for backward compat
    formik.setFieldValue('head_quarter_id', hqIds[0] ?? 0);
    // Don't validate assigned locations until allLocations is loaded —
    // otherwise validLocationIds is an empty Set and all selections get wiped on mount.
    if (!allLocations.length) return;
    // drop any assigned locations that no longer belong to selected HQs
    const validLocationIds = new Set(
      allLocations.filter(loc => hqIds.includes(Number(loc.head_quarter_id))).map(loc => Number(loc.id))
    );
    const currentAssigned: number[] = (Array.isArray(formik.values.assigned_locations)
      ? formik.values.assigned_locations
      : []).map((l: any) => typeof l === 'object' && l !== null ? Number(l.id) : Number(l)).filter(Boolean);
    const filtered = currentAssigned.filter(id => validLocationIds.has(id));
    if (filtered.length !== currentAssigned.length) {
      formik.setFieldValue('assigned_locations', filtered);
    }
  }, [JSON.stringify(formik.values.head_quarters), allLocations]);

  // Handle zone change — reload HQs and clear selection
  useEffect(() => {
    const selectedZone = formik.values.zone_id;
    if (selectedZone) {
      getheadquarterByZone(selectedZone);
      // Don't clear when initializing with existing employee data
      if (!isInitialized) return;
      formik.setFieldValue('head_quarters', []);
      formik.setFieldValue('head_quarter_id', 0);
    }
  }, [formik.values.zone_id]);

  // ── Out Station HQs → Out Stations (hierarchical) ────────────────────────
  // HQ-flagged locations available for the "Out Station HQs" picker
  const hqLocationOptions = useMemo(
    () => allLocations.filter(loc => !!loc.is_hq),
    [allLocations]
  );

  const selectedOutStationHqIds: number[] = Array.isArray(formik.values.out_station_hqs)
    ? formik.values.out_station_hqs.map(Number).filter(Boolean)
    : [];

  const selectedOutStationHqs = useMemo(
    () => hqLocationOptions.filter(loc => selectedOutStationHqIds.includes(Number(loc.id))),
    [hqLocationOptions, selectedOutStationHqIds]
  );

  // Candidate Out Station locations, grouped by selected Out Station HQ
  // (siblings under the same head_quarter_id, excluding other HQ-flagged locations)
  const outStationGroups = useMemo(() => {
    return selectedOutStationHqs.map(hq => ({
      hq,
      options: allLocations
        .filter(loc => !loc.is_hq && Number(loc.head_quarter_id) === Number(hq.head_quarter_id))
        .map(loc => ({ value: Number(loc.id), label: loc.name })),
    }));
  }, [selectedOutStationHqs, allLocations]);

  // Drop out_stations selections that no longer belong to any selected Out Station HQ
  useEffect(() => {
    if (!allLocations.length) return;
    const validIds = new Set(outStationGroups.flatMap(g => g.options.map(o => o.value)));
    const current: number[] = Array.isArray(formik.values.out_stations)
      ? formik.values.out_stations.map(Number)
      : [];
    const filtered = current.filter(id => validIds.has(id));
    if (filtered.length !== current.length) {
      formik.setFieldValue('out_stations', filtered);
    }
  }, [JSON.stringify(formik.values.out_station_hqs), allLocations]);

  // Update out_stations for a single HQ group without disturbing other groups' selections
  const handleOutStationGroupChange = (groupOptions: { value: number; label: string }[], selected: any) => {
    const groupIds = new Set(groupOptions.map(o => o.value));
    const newIds: number[] = selected ? selected.map((s: any) => Number(s.value)) : [];
    const current: number[] = Array.isArray(formik.values.out_stations)
      ? formik.values.out_stations.map(Number)
      : [];
    const withoutGroup = current.filter(id => !groupIds.has(id));
    formik.setFieldValue('out_stations', [...withoutGroup, ...newIds]);
  };

  // Assigned location options — locations under selected HQs (same set as exStations)
  const assignedLocationOptions = useMemo(
    () => exStations.map(loc => ({ value: Number(loc.id), label: loc.name })),
    [exStations]
  );

  const selectedAssignedLocations = useMemo(() => {
    const ids: number[] = (Array.isArray(formik.values.assigned_locations)
      ? formik.values.assigned_locations
      : []).map((l: any) => typeof l === 'object' && l !== null ? Number(l.id) : Number(l)).filter(Boolean);
    return assignedLocationOptions.filter(opt => ids.includes(opt.value));
  }, [formik.values.assigned_locations, assignedLocationOptions]);

  // Station options for react-select
  const exStationOptions = useMemo(() => {
    return exStations.map(station => ({
      value: Number(station.id),
      label: station.name,
    }));
  }, [exStations]);

  // Selected stations for react-select - FIXED VERSION
  const selectedExStations = useMemo(() => {
    console.log("Current ex_stations values:", formik.values.ex_stations);
    console.log("Available exStationOptions:", exStationOptions);

    if (!Array.isArray(formik.values.ex_stations) || !formik.values.ex_stations.length) {
      return [];
    }

    if (!exStationOptions.length) {
      return [];
    }

    const selected = exStationOptions.filter(option => {
      console.log("formik.values.ex_stations", formik.values.ex_stations)
      console.log("option.value", option.value)
      return formik.values.ex_stations.includes(option.value);
    });

    console.log("Selected ex stations for dropdown:", selected);
    return selected;
  }, [formik.values.ex_stations, exStationOptions]);

  // Selected Out Station HQs for react-select
  const selectedOutStationHqOptions = useMemo(() => {
    return hqLocationOptions
      .filter(loc => selectedOutStationHqIds.includes(Number(loc.id)))
      .map(loc => ({ value: Number(loc.id), label: loc.name }));
  }, [hqLocationOptions, selectedOutStationHqIds]);

  // Selected Out Stations for a given HQ group's dropdown
  const getSelectedOutStationsForGroup = (groupOptions: { value: number; label: string }[]) => {
    const current: number[] = Array.isArray(formik.values.out_stations)
      ? formik.values.out_stations.map(Number)
      : [];
    return groupOptions.filter(opt => current.includes(opt.value));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading stations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
        Contact Information
      </h3>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Contact Number *
          </label>
          <input
            type="tel"
            name="mobile"
            value={formik.values.mobile || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter Primary Contact Number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
          {formik.touched.mobile && formik.errors.mobile ? (
            <div className="text-red-700 text-sm mt-1">{formik.errors.mobile}</div>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alternate Contact Number
          </label>
          <input
            type="tel"
            name="alternate_mobile"
            value={formik.values.alternate_mobile || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter Alternate Contact Number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {formik.touched.alternate_mobile && formik.errors.alternate_mobile ? (
            <div className="text-red-700 text-sm mt-1">{formik.errors.alternate_mobile}</div>
          ) : null}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email *
        </label>
        <input
          type="email"
          name="email"
          value={formik.values.email || ""}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Enter Email"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {formik.touched.email && formik.errors.email ? (
          <div className="text-red-700 text-sm mt-1">{formik.errors.email}</div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Zone *
          </label>
          <select
            name="zone_id"
            value={formik.values.zone_id || ""}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Zone</option>
            {zones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
          {formik.touched.zone_id && formik.errors.zone_id ? (
            <div className="text-red-700 text-sm mt-1">{formik.errors.zone_id}</div>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select HQ *
          </label>
          <Select
            isMulti
            name="head_quarters"
            options={headquarters.map(hq => ({ value: Number(hq.id), label: hq.name }))}
            value={
              headquarters
                .filter(hq => (formik.values.head_quarters ?? []).includes(Number(hq.id)))
                .map(hq => ({ value: Number(hq.id), label: hq.name }))
            }
            onChange={(selected) => {
              const ids = selected ? selected.map(s => Number(s.value)) : [];
              formik.setFieldValue('head_quarters', ids);
            }}
            onBlur={() => formik.setFieldTouched('head_quarters', true)}
            placeholder="Select HQ(s)..."
            isLoading={isLoading}
            isDisabled={isLoading || !formik.values.zone_id}
            className="basic-multi-select"
            classNamePrefix="select"
          />
          {formik.touched.head_quarters && formik.errors.head_quarters ? (
            <div className="text-red-700 text-sm mt-1">{String(formik.errors.head_quarters)}</div>
          ) : null}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assign Locations
        </label>
        <Select
          isMulti
          name="assigned_locations"
          options={assignedLocationOptions}
          value={selectedAssignedLocations}
          className="basic-multi-select"
          classNamePrefix="select"
          onChange={(selected) => {
            const ids = selected ? selected.map(item => Number(item.value)) : [];
            formik.setFieldValue("assigned_locations", ids);
          }}
          onBlur={() => formik.setFieldTouched("assigned_locations", true)}
          placeholder={
            !formik.values.head_quarters?.length
              ? "Select HQ(s) first..."
              : assignedLocationOptions.length
              ? "Select locations to assign..."
              : "No locations available for selected HQ(s)"
          }
          isLoading={isLoading}
          isDisabled={isLoading || !formik.values.head_quarters?.length}
        />
        {formik.touched.assigned_locations && formik.errors.assigned_locations ? (
          <div className="text-red-700 text-sm mt-1">{String(formik.errors.assigned_locations)}</div>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Ex-Stations
        </label>
        <Select
          isMulti
          name="ex_stations"
          options={exStationOptions}
          value={selectedExStations}
          className="basic-multi-select"
          classNamePrefix="select"
          onChange={(selected) => {
            const ids = selected ? selected.map(item => Number(item.value)) : [];
            console.log("Setting ex_stations to:", ids);
            formik.setFieldValue("ex_stations", ids);
          }}
          onBlur={() => formik.setFieldTouched("ex_stations", true)}
          placeholder="Select Ex-Stations..."
          isLoading={isLoading}
          isDisabled={isLoading}
        />
        {formik.touched.ex_stations && formik.errors.ex_stations ? (
          <div className="text-red-700 text-sm mt-1">{formik.errors.ex_stations}</div>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Out Station HQs
        </label>
        <Select
          isMulti
          name="out_station_hqs"
          options={hqLocationOptions.map(loc => ({ value: Number(loc.id), label: loc.name }))}
          value={selectedOutStationHqOptions}
          className="basic-multi-select"
          classNamePrefix="select"
          onChange={(selected) => {
            const ids = selected ? selected.map(item => Number(item.value)) : [];
            formik.setFieldValue("out_station_hqs", ids);
          }}
          onBlur={() => formik.setFieldTouched("out_station_hqs", true)}
          placeholder="Select Out Station HQ(s)..."
          isLoading={isLoading}
          isDisabled={isLoading}
        />
        {formik.touched.out_station_hqs && formik.errors.out_station_hqs ? (
          <div className="text-red-700 text-sm mt-1">{String(formik.errors.out_station_hqs)}</div>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Out Stations
        </label>
        {outStationGroups.length === 0 ? (
          <p className="text-sm text-gray-500">Select Out Station HQ(s) above to choose out stations.</p>
        ) : (
          <div className="space-y-4">
            {outStationGroups.map(({ hq, options }) => (
              <div key={hq.id}>
                <label className="block text-xs font-medium text-gray-500 mb-1">{hq.name}</label>
                <Select
                  isMulti
                  options={options}
                  value={getSelectedOutStationsForGroup(options)}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  onChange={(selected) => handleOutStationGroupChange(options, selected)}
                  onBlur={() => formik.setFieldTouched("out_stations", true)}
                  placeholder={options.length ? `Select out stations under ${hq.name}...` : `No locations available under ${hq.name}`}
                  isLoading={isLoading}
                  isDisabled={isLoading || !options.length}
                />
              </div>
            ))}
          </div>
        )}
        {formik.touched.out_stations && formik.errors.out_stations ? (
          <div className="text-red-700 text-sm mt-1">{formik.errors.out_stations}</div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Address
          </label>
          <textarea
            name="current_address"
            value={formik.values.current_address || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter Current Address"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {formik.touched.current_address && formik.errors.current_address ? (
            <div className="text-red-700 text-sm mt-1">{formik.errors.current_address}</div>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Permanent Address
          </label>
          <textarea
            name="permanent_address"
            value={formik.values.permanent_address || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter Permanent Address"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {formik.touched.permanent_address && formik.errors.permanent_address ? (
            <div className="text-red-700 text-sm mt-1">{formik.errors.permanent_address}</div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zip/Postal Code
          </label>
          <input
            type="text"
            name="zip"
            value={formik.values.zip || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter Zip/Postal Code"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {formik.touched.zip && formik.errors.zip ? (
            <div className="text-red-700 text-sm mt-1">{formik.errors.zip}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};